import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { useTranslation } from '../../../lib/i18n';
import { socialAPI } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Instagram, Facebook, Youtube, Linkedin, Twitter, Music2,
  Link2, ExternalLink, CheckCircle2, Loader2,
} from 'lucide-react';

/**
 * Step 6: connect social accounts.
 *
 * Surfaces every platform Upload Post supports so an agent can see at a
 * glance where their auto-posts will go. The actual auth flow is shared
 *. A single Upload Post connect URL covers all six. So the cards here
 * are mostly informational + a per-platform "connected" indicator that
 * fills in once the user finishes the popup flow.
 *
 * Background tile gradient is rendered via inline style instead of a
 * Tailwind class because Tailwind doesn't have a native TikTok / X-style
 * gradient and we want the brand colours to read at a glance.
 */
const PLATFORMS = [
  {
    key: 'instagram',
    name: 'Instagram',
    icon: Instagram,
    iconBg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    accent: 'border-pink-500/40',
  },
  {
    key: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    iconBg: 'bg-blue-600',
    accent: 'border-blue-500/40',
  },
  {
    key: 'tiktok',
    name: 'TikTok',
    icon: Music2,
    // TikTok's recognisable cyan + magenta on near-black.
    iconBg: 'bg-gradient-to-br from-cyan-400 via-[#fffdf7] to-pink-500',
    accent: 'border-cyan-400/40',
  },
  {
    key: 'youtube',
    name: 'YouTube',
    icon: Youtube,
    iconBg: 'bg-[#a34430]',
    accent: 'border-[#b3543f]/50',
  },
  {
    key: 'x',
    name: 'X',
    // Lucide ships a Twitter icon; X is the rebrand so we use it under
    // the new name. Solid black tile to nail the current brand look.
    icon: Twitter,
    iconBg: 'bg-black',
    accent: 'border-white/30',
  },
  {
    key: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    iconBg: 'bg-[#0A66C2]',
    accent: 'border-blue-400/40',
  },
];

export default function SocialsStep({ connectedSocials, onSocialsChange }) {
  const { t } = useTranslation();
  const [connecting, setConnecting] = useState(false);
  // Loading state isn't surfaced visually anymore. Empty grid + the
  // single Connect button is enough. But we still track it to avoid a
  // flash of "Not connected" labels before the API call returns.
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);

  const loadAccounts = useCallback(async () => {
    try {
      const res = await socialAPI.getAccounts();
      const accounts = res.data.accounts || [];
      onSocialsChange(accounts);
    } catch (err) {
      // Silent. Empty list is the normal first-run state.
    } finally {
      setLoading(false);
    }
  }, [onSocialsChange]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Listen for popup close
  useEffect(() => {
    const handler = (e) => {
      if (e.data === 'social-connected') loadAccounts();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [loadAccounts]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await socialAPI.connect();
      const { access_url } = res.data;
      const popup = window.open(
        access_url,
        'connect-social',
        'width=600,height=700,left=200,top=100'
      );
      const timer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(timer);
          setConnecting(false);
          loadAccounts();
        }
      }, 1000);
    } catch (err) {
      const detail = err.response?.data?.detail || t('social.connectionFailed');
      toast.error(detail);
      setConnecting(false);
    }
  };

  const connectedPlatforms = connectedSocials.map((a) => a.platform);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex p-4 bg-blue-600/20 rounded-2xl mb-4">
          <Link2 className="w-10 h-10 text-blue-400" />
        </div>
        <h2 className="text-2xl font-bold text-[#23211a] mb-2">
          {t('onboarding.step6.title')}
        </h2>
        <p className="text-[#6f6a5c]">
          {t('onboarding.step6.subtitleAll') ||
            'Verbinde alle Konten, auf denen deine Auto-Posts erscheinen sollen. Instagram, Facebook, TikTok, YouTube, X und LinkedIn.'}
        </p>
      </div>

      {/* Platform grid. 2 cols mobile, 3 cols on tablet+ so the 6 cards
          land cleanly as a 2x3 block. */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {PLATFORMS.map((platform) => {
          const isConnected = connectedPlatforms.includes(platform.key);
          const Icon = platform.icon;
          const account = connectedSocials.find((a) => a.platform === platform.key);

          return (
            <div
              key={platform.key}
              className={`p-4 rounded-xl border-2 transition-all ${
                isConnected
                  ? `${platform.accent} bg-white`
                  : 'border-[#e3dccb] bg-[#f2ecdf]'
              }`}
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className={`p-2 rounded-lg shrink-0 ${platform.iconBg}`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#23211a] text-sm truncate">{platform.name}</p>
                  {isConnected && account && (
                    <p className="text-[10px] text-[#6f6a5c] truncate">
                      {account.display_name || account.username}
                    </p>
                  )}
                </div>
              </div>

              {isConnected ? (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-[#e4f0e8] border border-[#1e7a4f]/40 rounded-md">
                  <CheckCircle2 className="w-3 h-3 text-[#1e7a4f] shrink-0" />
                  <span className="text-[11px] text-[#1e7a4f] font-medium truncate">
                    {t('common.connected')}
                  </span>
                </div>
              ) : (
                <div className="px-2 py-1 bg-[#f2ecdf] rounded-md">
                  <p className="text-[11px] text-[#8a8474]">
                    {t('common.notConnected')}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Connected summary */}
      {connectedSocials.length > 0 && (
        <div className="flex items-center justify-center gap-2 text-[#1e7a4f] text-sm">
          <CheckCircle2 className="w-4 h-4" />
          {t('onboarding.step6.connectedAccounts', { count: connectedSocials.length })}
        </div>
      )}

      {/* Connect button. Single Upload Post flow handles every platform,
          so the copy is platform-agnostic instead of "Instagram & Facebook". */}
      <div className="text-center">
        <Button
          onClick={handleConnect}
          disabled={connecting}
          className="bg-[#1f4d3a] hover:bg-[#163a2c] px-8"
        >
          {connecting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('social.connecting')}
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4 mr-2" />
              {connectedSocials.length > 0
                ? t('social.connectAccount')
                : t('onboarding.step6.connectAll') || 'Konten verbinden'}
            </>
          )}
        </Button>
        <p className="text-xs text-[#8a8474] mt-2">
          {t('onboarding.step6.connectDesc')}
        </p>
      </div>
    </div>
  );
}
